import {
	ILocationDiagram,
	IRackDetail,
	IShelfWithLocations,
} from '../../../../redux/api/inventory';

/** Color for an occupancy percentage (matches the table progress semantics). */
export function occupancyColor(pct: number): string {
	if (pct <= 0) return '#fafafa'; // empty
	if (pct < 50) return '#b7eb8f'; // green
	if (pct < 85) return '#ffe58f'; // amber
	if (pct <= 100) return '#ffa39e'; // red
	return '#ff4d4f'; // over capacity
}

/** Weight-based fill percentage for a shared shelf band. */
export function shelfWeightPercent(shelf: IShelfWithLocations): number {
	if (!shelf.max_weight || shelf.max_weight <= 0) return 0;
	return Math.round(((shelf.occupied_weight || 0) / shelf.max_weight) * 100);
}

/** Occupancy for a shared shelf band. Shared shelves are weight-controlled. */
export function shelfOccupancyPercent(shelf: IShelfWithLocations): number {
	return shelfWeightPercent(shelf);
}

/** Format a numeric dimension dropping trailing zeros (2.00 → 2, 1.50 → 1.5). */
function fmtDim(value: number | null | undefined): string {
	if (value == null) return '—';
	const n = parseFloat(String(value));
	return Number.isNaN(n) ? '—' : String(n);
}

/** Compact human label for a location's capacity (e.g. "2 × 1.5 in"). */
export function locationCapacityLabel(loc: ILocationDiagram): string {
	if (loc.max_width != null || loc.max_height != null) {
		return `${fmtDim(loc.max_width)} × ${fmtDim(loc.max_height)} in`;
	}
	if (loc.max_volume != null) {
		return `${fmtDim(loc.max_volume)} in³`;
	}
	return '';
}

/* ================================
   Slot grid (Excel-like matrix)
================================ */

/** Parse a code like "A1" / "B12" into its column letter and row number. */
export function parseSlotCode(
	code: string,
): { col: string; row: number } | null {
	const match = /^([A-Za-z]+)(\d+)$/.exec((code || '').trim());
	if (!match) return null;
	return { col: match[1].toUpperCase(), row: parseInt(match[2], 10) };
}

export interface ISlotGridCell {
	location: ILocationDiagram;
	shelf: IShelfWithLocations;
}

/**
 * A single shelf's own slot matrix. Columns/rows/cells are namespaced to this
 * shelf, so two shelves can reuse the same codes (e.g. both start at "A1")
 * without colliding. `extras` holds locations whose code doesn't fit the
 * letter+number convention (e.g. the "Shelf 0" catch-all, code "0").
 */
export interface IShelfSlotGrid {
	shelf: IShelfWithLocations;
	/** Column letters used by this shelf, sorted (e.g. A, B, C…). */
	columns: string[];
	/** Row numbers used by this shelf, sorted ascending (1 at top). */
	rows: number[];
	/** Cell lookup keyed by `${col}-${row}` within this shelf only. */
	cells: Record<string, ISlotGridCell>;
	/** Non-grid locations for this shelf, rendered as a band. */
	extras: ILocationDiagram[];
}

export interface ISlotGrid {
	/** One matrix per shelf, in rack (top-to-bottom) order. */
	shelves: IShelfSlotGrid[];
}

/**
 * Build a per-shelf slot matrix (columns = code letter, rows = code number).
 * Each shelf keeps its own columns/rows/cells so codes never collide across
 * shelves. Locations whose code doesn't follow the letter+number convention
 * are collected in that shelf's `extras`. Returns null only when NO shelf has
 * any grid-shaped location, so the caller can fall back to the linear layout.
 */
export function buildSlotGrid(rack: IRackDetail): ISlotGrid | null {
	const shelves: IShelfSlotGrid[] = [];

	for (const shelf of rack.shelves) {
		const cells: Record<string, ISlotGridCell> = {};
		const colSet = new Set<string>();
		const rowSet = new Set<number>();
		const extras: ILocationDiagram[] = [];

		for (const location of shelf.locations) {
			const parsed = parseSlotCode(location.code);
			if (!parsed) {
				// Non-grid location (e.g. shared-shelf or catch-all): render as a band.
				extras.push(location);
				continue;
			}
			colSet.add(parsed.col);
			rowSet.add(parsed.row);
			cells[`${parsed.col}-${parsed.row}`] = { location, shelf };
		}

		// Always include the shelf — even when it is empty (no grid cells and no
		// extras). An empty shelf renders as a placeholder row in SlotGrid so it
		// stays visible and manageable (add location / edit / delete). Dropping it
		// here is what made empty shelves disappear once a sibling shelf had
		// locations and the diagram switched to the matrix layout (PTB-5919).
		shelves.push({
			shelf,
			columns: [...colSet].sort(),
			rows: [...rowSet].sort((a, b) => a - b),
			cells,
			extras,
		});
	}

	// Render the matrix whenever the rack has at least one shelf; only fall back
	// to the linear layout when the rack has no shelves at all (handled upstream
	// by RackVisual's empty state, so this is effectively defensive).
	if (!shelves.length) return null;

	return { shelves };
}
